import { useState, useEffect } from 'react'
import axios from 'axios'
import Link from 'next/link'
import Markdown from 'markdown-to-jsx'
import { NextSeo } from 'next-seo'
import ChevronRightSmall24 from '@evocolabs/icons/lib/ChevronRightSmall24'
import AudiologistOrka64 from '@evocolabs/icons/lib/AudiologistOrka64'
import { InteractiveButton, Typography } from '@evocolabs/orkaui'
import theme from '@evocolabs/orkaui/lib/theme.cjs'
import { useBackend } from '../../src/providers/BackendProvider'
import Header from '@/src/components/HeaderV2'
import Footer from '@/src/components/Footer/Footer2'
import styles from '@/src/components/scss/TroubleShooting/[id].module.scss'

import { parse2UpperDevice, parseSection } from '@/src/utils/troubleshooting.js'

const deviceMap = {
  orka_one: 'Orka One',
  orka_two: 'Orka Two'
}

export default function TroublePage({ data, qstId }) {
  const { updateTroubleShootingComment } = useBackend()
  const [alreadyLike, setAlreadyLike] = useState(false)
  const [alreadydisLike, setAlreadydisLike] = useState(false)

  useEffect(() => {
    const troubleshooting =
      JSON.parse(localStorage.getItem('troubleshooting') || 0) || {}
    setAlreadyLike(troubleshooting[`like_${data.id}`] === 'true')
    setAlreadydisLike(troubleshooting[`dislike_${data.id}`] === 'true')
  }, [data?.id])

  const updateFeedback = async (value, updater, actionType, action) => {
    try {
      updater(value)
      const res = await updateTroubleShootingComment(
        data.id,
        actionType,
        action
      )
      let troubleshooting =
        JSON.parse(localStorage.getItem('troubleshooting') || 0) || {}
      troubleshooting[`${action}_${data.id}`] = String(value)
      localStorage.setItem('troubleshooting', JSON.stringify(troubleshooting))
    } catch (error) {
      console.error(error)
    }
  }

  const like = async () => {
    if (data.question) {
      // make sure already loaded
      if (!alreadyLike) {
        // like
        data.like = data.like + 1
        data.total = data.total + 1
        if (alreadydisLike) {
          // cancel dislike
          data.total = data.total - 1
          await updateFeedback(false, setAlreadydisLike, 1, 'dislike')
        }
        await updateFeedback(true, setAlreadyLike, 0, 'like')
      } else {
        // cancel like
        data.total = data.total - 1
        data.like = data.like - 1
        await updateFeedback(false, setAlreadyLike, 1, 'like')
      }
    }
  }
  const dislike = async () => {
    if (data.question) {
      // make sure already loading
      if (!alreadydisLike) {
        // dislike
        data.total = data.total + 1
        if (alreadyLike) {
          // cancel like
          data.like = data.like - 1
          data.total = data.total - 1
          await updateFeedback(false, setAlreadyLike, 1, 'like')
        }
        await updateFeedback(true, setAlreadydisLike, 0, 'dislike')
      } else {
        // cancel dislike
        data.total = data.total - 1
        await updateFeedback(false, setAlreadydisLike, 1, 'dislike')
      }
    }
  }

  return (
    <div>
      <NextSeo
        title="TroubleShooting | Orka Hearing Aids"
        description="Have questions about Orka or your hearing health? We are here for you."
      />
      <Header isWhiteBg />
      <div className={styles.body_wrapper}>
        <nav>
          <Typography variant="Body3" color={theme.palette.gray[800]}>
            <Link href="/TroubleShooting">Trouble Shooting</Link>
          </Typography>
          <ChevronRightSmall24 forceStroke={theme.palette.gray[800]} />
          <Typography variant="Body3" color={theme.palette.gray[800]}>
            <Link href={`/TroubleShooting?device=${deviceMap[data?.device]}`}>
              {deviceMap[data?.device]}
            </Link>
          </Typography>
          <ChevronRightSmall24 forceStroke={theme.palette.gray[400]} />
          <Typography
            variant="Body3"
            color={theme.palette.gray[400]}
            dangerouslySetInnerHTML={{ __html: data.question }}
          ></Typography>
        </nav>
        <Typography
          variant="h2"
          color={theme.palette.gray[800]}
          mb={3}
          dangerouslySetInnerHTML={{ __html: data.question }}
        ></Typography>
        <hr
          style={{
            backgroundColor: theme.palette.grey[300],
            marginBottom: '40px'
          }}
        />
        <Markdown style={{ fontSize: '18px' }}>{data?.content}</Markdown>
        <hr
          style={{
            backgroundColor: theme.palette.grey[300],
            margin: '32px 0'
          }}
        />
        <div>
          <Typography color={theme.palette.gray[800]} variant={'Body1'}>
            Was this article helpful ?
          </Typography>
        </div>
        <div style={{ display: 'flex' }}>
          <InteractiveButton
            type="thumb_up"
            isActive={alreadyLike}
            onClick={() => like()}
          />
          <InteractiveButton
            type="thumb_down"
            isActive={alreadydisLike}
            onClick={() => dislike()}
          />
        </div>
        <div>
          <Typography variant="body4" color={theme.palette.gray[300]}>
            {data ? data.like : 0} out of {data ? data.total : 0} found this
            helpful
          </Typography>
        </div>
        <hr
          style={{
            backgroundColor: theme.palette.grey[300],
            margin: '32px 0'
          }}
        />
        <div className={styles.tip_card}>
          <AudiologistOrka64 />
          <Typography variant="Body1">
            If the issues persist, please contact us for remote consultation and
            more support.
          </Typography>
        </div>
      </div>
      <Footer />
    </div>
  )
}
export async function getStaticPaths() {
  const url = process.env.NEXT_PUBLIC_BASEURL + 'troubleshooting-article/'
  const res = await fetch(url)
  const data = await res.json()
  const troubleShootingItems = []
  Object.values(data).forEach((ls) => {
    ls.forEach((d) => {
      troubleShootingItems.push(d)
    })
  })
  return {
    paths: troubleShootingItems.map((d) => {
      return {
        params: {
          subPath: [
            parse2UpperDevice(d.tags['device']),
            parseSection(d.tags['section']),
            d.id.toString()
          ]
        }
      }
    }),
    fallback: false
  }
}

export async function getStaticProps({ params }) {
  let qstId = params.subPath[2]

  const res = await axios.get(
    process.env.NEXT_PUBLIC_BASEURL + 'troubleshooting-article/' + qstId + '/'
  )
  return { props: { data: res.data, qstId: qstId }, revalidate: 600 }
}
